import { FC, useCallback, useState } from 'react';
import { List, ListItemButton, ListItemText, ListSubheader, Typography } from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { useHistory } from 'react-router-dom';

import ticketsApi from '../../api/tickets/api';
import type { Ticket } from '../../api/tickets/types';
import { getDateTimeString } from '../../utils/dateUtils';
import HeaderCountButton from './HeaderCountButton';

const BOARD_PATH = '/administration/setup/tickets';
const MY_TICKETS_PATH = '/my-tickets';
/** The popover is a peek, not the board — the newest few, and the footer opens the rest. */
const PREVIEW_LIMIT = 8;
/** Closed as far as the reporter is concerned — a reply still reopens them, but nothing is pending. */
const CLOSED_STATUSES = ['done', 'wont_fix'];

/** Reporter-facing, as on My Tickets: `awaiting_reporter` is phrased as what it asks of them. */
const MY_STATUS_LABELS: Record<string, string> = {
    new: 'New',
    triaged: 'Triaged',
    in_progress: 'In Progress',
    awaiting_reporter: 'Awaiting your reply',
    done: 'Done',
    wont_fix: "Won't Fix"
};

type MyTicketRow = Ticket & { unreadCount?: number };

const needsMe = (t: MyTicketRow) => t.status === 'awaiting_reporter' || (t.unreadCount || 0) > 0;

interface Props {
    /** Holds `system.tickets:view` — also sees the tenant's untriaged tickets. */
    canTriage: boolean;
}

/**
 * The header ticket icon, for every user. Everyone sees their OWN open tickets, served by the
 * `/tickets/mine` endpoints, which are scoped to the session's user and tenant whatever the client
 * asks. A triager additionally sees the tenant's tickets still in `new` — from the board endpoints,
 * which enforce `system.tickets:view` themselves; `canTriage` only decides whether to ask.
 *
 * The badge counts what is waiting on the viewer: their tickets awaiting their reply or carrying an
 * unread reply, plus — for a triager — untriaged tickets and tickets a reporter has replied on.
 */
const TicketsIndicator: FC<Props> = ({ canTriage }) => {
    const history = useHistory();
    const [myCount, setMyCount] = useState(0);
    const [newCount, setNewCount] = useState(0);
    const [repliedCount, setRepliedCount] = useState(0);
    const [myTickets, setMyTickets] = useState<MyTicketRow[]>([]);
    const [newTickets, setNewTickets] = useState<Ticket[]>([]);
    const [repliedTickets, setRepliedTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(() => {
        ticketsApi
            .countMyAttention()
            .then(setMyCount)
            .catch(() => {});
        if (canTriage) {
            ticketsApi
                .countNewTickets()
                .then(setNewCount)
                .catch(() => {});
            ticketsApi
                .countRepliedTickets()
                .then(setRepliedCount)
                .catch(() => {});
        }
    }, [canTriage]);

    const loadPreview = useCallback(() => {
        setLoading(true);
        const mine = ticketsApi.listMyTickets().then((rows: MyTicketRow[]) => {
            // Open tickets, plus a closed one with a reply not yet read — that reply is still news.
            const open = (rows || []).filter((t) => !CLOSED_STATUSES.includes(t.status) || needsMe(t));
            // Waiting on them first; listMyTickets is already newest-first within each group.
            open.sort((a, b) => Number(needsMe(b)) - Number(needsMe(a)));
            setMyTickets(open.slice(0, PREVIEW_LIMIT));
        });
        const untriaged = canTriage
            ? ticketsApi
                  .listTickets({ status: 'new', limit: String(PREVIEW_LIMIT) })
                  .then((rows) => setNewTickets(rows || []))
            : Promise.resolve();
        const replied = canTriage
            ? ticketsApi
                  .listTickets({ replied: 'true', limit: String(PREVIEW_LIMIT) })
                  .then((rows) => setRepliedTickets(rows || []))
            : Promise.resolve();
        Promise.allSettled([mine, untriaged, replied]).finally(() => setLoading(false));
        refresh();
    }, [canTriage, refresh]);

    /** A board list in the popover — rows open the ticket on the board. */
    const renderBoardSection = (
        label: string,
        count: number,
        rows: Ticket[],
        emptyText: string,
        close: () => void
    ) => (
        <List dense disablePadding subheader={<ListSubheader disableSticky>{`${label} (${count})`}</ListSubheader>}>
            {rows.length ? (
                rows.map((t) => (
                    <ListItemButton
                        key={t._id}
                        onClick={() => {
                            close();
                            history.push(`${BOARD_PATH}?ticket=${t._id}`);
                        }}
                    >
                        <ListItemText
                            primary={t.title || t.description || t.ticketNumber || `#${t._id}`}
                            primaryTypographyProps={{ noWrap: true }}
                            secondary={[t.ticketNumber, t.createdByName, getDateTimeString(t.createdAt as string)]
                                .filter(Boolean)
                                .join(' · ')}
                        />
                    </ListItemButton>
                ))
            ) : (
                <Typography sx={{ px: 2, pb: 1.5, color: 'text.secondary', fontSize: '0.85rem' }}>
                    {emptyText}
                </Typography>
            )}
        </List>
    );

    const footer = [{ label: 'Open My Tickets', onClick: () => history.push(MY_TICKETS_PATH) }];
    if (canTriage) {
        footer.push({ label: 'Open ticket board', onClick: () => history.push(`${BOARD_PATH}?status=new`) });
    }

    return (
        <HeaderCountButton
            title="Tickets"
            icon={<ConfirmationNumberOutlinedIcon fontSize="small" />}
            count={myCount + (canTriage ? newCount + repliedCount : 0)}
            color="error"
            refresh={refresh}
            // `tickets:mine-changed` goes to this user's room only; the tenant-wide `tickets:changed`
            // matters only to a triager, so nobody else re-asks on every report filed.
            refreshOn={canTriage ? ['tickets:mine-changed', 'tickets:changed'] : ['tickets:mine-changed']}
            onOpen={loadPreview}
            loading={loading}
            footer={footer}
        >
            {(close) => (
                <>
                    {canTriage && (
                        <>
                            {renderBoardSection('User replied', repliedCount, repliedTickets, 'No unread replies.', close)}
                            {renderBoardSection('Needs triage', newCount, newTickets, 'No new tickets.', close)}
                        </>
                    )}
                    <List
                        dense
                        disablePadding
                        subheader={
                            canTriage ? <ListSubheader disableSticky>My tickets</ListSubheader> : undefined
                        }
                    >
                        {myTickets.length ? (
                            myTickets.map((t) => {
                                const unread = t.unreadCount || 0;
                                const awaiting = t.status === 'awaiting_reporter';
                                return (
                                    <ListItemButton
                                        key={t._id}
                                        onClick={() => {
                                            close();
                                            history.push(`${MY_TICKETS_PATH}?ticket=${t._id}`);
                                        }}
                                    >
                                        <ListItemText
                                            primary={t.title || t.description || t.ticketNumber || `#${t._id}`}
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                fontWeight: needsMe(t) ? 600 : 400
                                            }}
                                            secondary={[
                                                MY_STATUS_LABELS[t.status] || t.status,
                                                unread ? `${unread} new repl${unread > 1 ? 'ies' : 'y'}` : null,
                                                t.ticketNumber
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                            secondaryTypographyProps={
                                                awaiting ? { sx: { color: '#f97316', fontWeight: 600 } } : undefined
                                            }
                                        />
                                    </ListItemButton>
                                );
                            })
                        ) : (
                            <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
                                You have no open tickets.
                            </Typography>
                        )}
                    </List>
                </>
            )}
        </HeaderCountButton>
    );
};

export default TicketsIndicator;
